// src/pages/settings/BankSettings.tsx
import {
  Box,
  Button,
  EmptyState,
  Flex,
  HStack,
  Input,
  Text,
  IconButton,
  Separator,
  Table,
  Skeleton,
  SkeletonText,
  Stack,
  InputGroup,
  Menu,
  Portal,
} from '@chakra-ui/react'
import { useEffect, useMemo, useState } from 'react'
import {
  LuBuilding2,
  LuPlus,
  LuChevronLeft,
  LuChevronRight,
  LuSearch,
  LuCheck,
  LuListFilter,
} from 'react-icons/lu'
import { toast } from '@/components/ui/toaster/toasterHelpers'
import type { Bank } from '@/common/settings.common'
import { createBank, updateBank, getServices } from '@/api/settings.api'
import type {
  CreateBankPayload,
  BankPartialUpdatePayload,
  ServiceCatalogItem,
} from '@/api/dto/settings.dto'
import { CreateEditBankDialog } from '@/components/modals/CreateEditBankDialog'
import { UNION_BANK_ID } from '@/common/unionBank.common'
import { API_BASE_URL } from '@/config'
import { FormLayout } from '@/components/layout/FormLayout'

export default function BankSettings() {
  type SortDir = 'asc' | 'desc'
  type SortFieldUi = 'name' | 'servicesCount' | 'region'
  const [sortField, setSortField] = useState<SortFieldUi>('name')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  // list state (server-driven)
  const [items, setItems] = useState<Bank[]>([])
  const [totalItems, setTotalItems] = useState<number | null>(null)
  const [page, setPage] = useState(1)
  const pageSize = 10

  const [search, setSearch] = useState('')
  const [sortDir, setSortDir] = useState<SortDir>('asc')

  // dialogs
  const [createOpen, setCreateOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [selected, setSelected] = useState<Bank | null>(null)

  // services catalog
  const [services, setServices] = useState<ServiceCatalogItem[]>([])

  // The Union parent bank is protected: master data is fixed and its prices are date-versioned.
  const PROTECTED_BANK_ID = UNION_BANK_ID

  const isProtectedBank = selected?.id === PROTECTED_BANK_ID

  const serviceOptions = useMemo(
    () => services.map(s => ({ label: s.name, value: s.code })),
    [services]
  )

  async function reloadServices() {
    const list = await getServices()
    setServices(
      [...list]
        .filter(s => s.active)
        .sort(
          (a, b) =>
            (a.displayOrder ?? 9999) - (b.displayOrder ?? 9999) || a.name.localeCompare(b.name)
        )
    )
  }
  function mapSortFieldToApi(field: SortFieldUi): 'name' | 'companyCode' {
    //  only these exist in API
    if (field === 'name') return 'name'
    // fallback (until backend supports more)
    return 'name'
  }
  async function reloadBanks(next?: {
    page?: number
    search?: string
    sortDir?: SortDir
    sortField?: SortFieldUi
  }) {
    const p = next?.page ?? page
    const s = next?.search ?? search
    const dir = next?.sortDir ?? sortDir
    const sf = next?.sortField ?? sortField

    const query = new URLSearchParams()
    query.set('parentId', '0')
    query.set('page', String(p))
    query.set('pageSize', String(pageSize))
    query.set('status', 'ACTIVE')
    query.set('sortBy', mapSortFieldToApi(sf))
    query.set('sortDir', dir)

    if (s.trim()) query.set('search', s.trim())

    // 👇 cache buster to defeat any promise-dedupe / caching layers
    query.set('_ts', String(Date.now()))

    const res = await fetch(`${API_BASE_URL}/settings/banks?${query.toString()}`, {
      method: 'GET',
      credentials: 'include',
      cache: 'no-store',
      headers: {
        Accept: 'application/json',
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
      },
    })

    if (!res.ok) {
      throw new Error(`Failed to fetch banks: ${res.status}`)
    }

    const data = (await res.json()) as any // PaginationResult<Bank> shape

    const nextItems: Bank[] = data.items ?? []
    setItems(nextItems)
    setTotalItems(typeof data.totalItems === 'number' ? data.totalItems : null)
    // Keep an open dialog on fresh data, e.g. planned price changes after a save or drop.
    setSelected(prev => (prev ? (nextItems.find(b => b.id === prev.id) ?? prev) : prev))
  }

  useEffect(() => {
    async function load() {
      try {
        setLoading(true)
        setError(false)
        await Promise.all([reloadServices(), reloadBanks({ page: 1 })])
      } catch {
        setError(true)
        toast.error('Auftraggeber konnten nicht geladen werden.')
      } finally {
        setLoading(false)
      }
    }
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // reload when paging/sorting
  useEffect(() => {
    if (loading) return
    reloadBanks().catch(() => {
      setError(true)
      toast.error('Auftraggeber konnten nicht geladen werden.')
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, sortDir, sortField])

  const totalPages = useMemo(() => {
    if (!totalItems) return null
    return Math.max(1, Math.ceil(totalItems / pageSize))
  }, [totalItems])

  const canPrev = page > 1
  const canNext = totalPages ? page < totalPages : items.length === pageSize // fallback
  const showPagination = (totalItems ?? 0) > pageSize

  const viewItems = useMemo(() => {
    let list = [...items]

    // client-side search (same idea as Union)
    const s = search.trim().toLowerCase()
    if (s) {
      list = list.filter(b => {
        const addr = `${b.postal ?? ''} ${b.city ?? ''} ${b.country ?? ''}`.toLowerCase()
        const contact =
          `${b.contactDetails?.contactName ?? ''} ${b.contactDetails?.contactEmail ?? ''}`.toLowerCase()
        return b.name.toLowerCase().includes(s) || addr.includes(s) || contact.includes(s)
      })
    }

    // client-side sorting (see section B for sort state)
    const dir = sortDir === 'asc' ? 1 : -1
    list.sort((a, b) => {
      if (sortField === 'servicesCount') {
        const av = a.bankServices?.length ?? 0
        const bv = b.bankServices?.length ?? 0
        return (av - bv) * dir
      }

      if (sortField === 'region') {
        const aKey = `${a.postal ?? ''} ${a.city ?? ''} ${a.country ?? ''}`.trim().toLowerCase()
        const bKey = `${b.postal ?? ''} ${b.city ?? ''} ${b.country ?? ''}`.trim().toLowerCase()
        if (aKey < bKey) return -1 * dir
        if (aKey > bKey) return 1 * dir
        return 0
      }

      // default: name
      const aKey = (a.name ?? '').toLowerCase()
      const bKey = (b.name ?? '').toLowerCase()
      if (aKey < bKey) return -1 * dir
      if (aKey > bKey) return 1 * dir
      return 0
    })

    return list
  }, [items, search, sortField, sortDir])

  function applySearch() {
    setPage(1)
    reloadBanks({ page: 1, search }).catch(() => {
      setError(true)
      toast.error('Auftraggeber konnten nicht geladen werden.')
    })
  }

  async function ensureService({ name, code }: { name: string; code: string }) {
    // backend createService is intentionally not used here; we only refresh catalog
    const exists = services.some(s => s.code.toUpperCase() === code.toUpperCase())
    if (exists) return { name, code }
    await reloadServices()
    return { name, code }
  }

  if (loading) {
    return (
      <Flex direction="column" p={2} gap={4}>
        <Stack direction={{ base: 'column', md: 'row' }} justify="space-between" align="flex-end">
          <SkeletonText noOfLines={1} w="240px" />
          <Skeleton h="32px" w={{ base: 'full', md: '240px' }} />
        </Stack>
        <Skeleton h="44px" w="full" />
        <Skeleton h="360px" w="full" />
      </Flex>
    )
  }

  if (error) {
    return (
      <Box p="4">
        <Text color="fg.error" textStyle="body">
          Fehler beim Laden der Auftraggeber.
        </Text>
      </Box>
    )
  }

  return (
    <FormLayout.Root>
      <Flex gap={6} align="flex-start">
        {/* RIGHT MAIN */}
        <FormLayout.Root flex="1" minW={0}>
          <Flex direction="column" gap={4}>
            <FormLayout.Title>Auftraggeber</FormLayout.Title>
            <FormLayout.Description>
              Gut gepflegte Auftraggeberdaten erleichtern die Abrechnung erheblich. Sobald
              Aktivitäten einem Auftraggeber zugeordnet sind, können Rechnungen schneller und mit
              weniger manueller Nacharbeit erstellt werden. Hinterlegen Sie Ansprechpartner,
              Rechnungsadresse und individuelle Leistungen mit Preisen pro Einheit. Besonders bei
              externen Auftraggebern sorgt eine saubere Datenbasis für Klarheit, korrekte
              Rechnungen und weniger Abstimmungsaufwand.
            </FormLayout.Description>
          </Flex>

          <Flex direction="column" gap={4}>
            {/* Header row: search + sort + create */}
            <Flex
              direction={{ base: 'column', md: 'row' }}
              align={{ base: 'stretch', md: 'center' }}
              justify="space-between"
              gap={2}
            >
              <Flex
                direction={{ base: 'column', sm: 'row' }}
                align="stretch"
                gap={3}
                flex="1"
                minW={0}
              >
                <InputGroup minW={0} flex="1" startElement={<LuSearch />}>
                  <Input
                    placeholder="Auftraggeber durchsuchen"
                    value={search}
                    w="full"
                    onChange={e => setSearch(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') applySearch()
                    }}
                  />
                </InputGroup>
                <Menu.Root>
                  <Menu.Trigger asChild>
                    <Button variant="subtle">
                      <HStack gap={2}>
                        <LuListFilter />
                        <Text>Sortierung</Text>
                      </HStack>
                    </Button>
                  </Menu.Trigger>

                  <Portal>
                    <Menu.Positioner>
                      <Menu.Content minW="280px">
                        {/* sort field */}
                        <Menu.Item
                          value="name"
                          closeOnSelect={false}
                          onClick={() => setSortField('name')}
                        >
                          <Flex w="full" align="center" justify="space-between">
                            <Text>Alphabetisch</Text>
                            {sortField === 'name' && <LuCheck />}
                          </Flex>
                        </Menu.Item>

                        <Menu.Item
                          value="servicesCount"
                          closeOnSelect={false}
                          onClick={() => setSortField('servicesCount')}
                        >
                          <Flex w="full" align="center" justify="space-between">
                            <Text>Anzahl Leistungen</Text>
                            {sortField === 'servicesCount' && <LuCheck />}
                          </Flex>
                        </Menu.Item>

                        <Menu.Item
                          value="region"
                          closeOnSelect={false}
                          onClick={() => setSortField('region')}
                        >
                          <Flex w="full" align="center" justify="space-between">
                            <Text>Region</Text>
                            {sortField === 'region' && <LuCheck />}
                          </Flex>
                        </Menu.Item>

                        <Menu.Separator />

                        <Menu.Item
                          value="asc"
                          closeOnSelect={false}
                          onClick={() => setSortDir('asc')}
                        >
                          <Flex w="full" align="center" justify="space-between">
                            <Text>Aufsteigend</Text>
                            {sortDir === 'asc' && <LuCheck />}
                          </Flex>
                        </Menu.Item>

                        <Menu.Item
                          value="desc"
                          closeOnSelect={false}
                          onClick={() => setSortDir('desc')}
                        >
                          <Flex w="full" align="center" justify="space-between">
                            <Text>Absteigend</Text>
                            {sortDir === 'desc' && <LuCheck />}
                          </Flex>
                        </Menu.Item>
                      </Menu.Content>
                    </Menu.Positioner>
                  </Portal>
                </Menu.Root>
              </Flex>

              <Button variant="solid" colorPalette="blue" onClick={() => setCreateOpen(true)}>
                <LuPlus />
                <Text>Neuen Auftraggeber anlegen</Text>
              </Button>
            </Flex>

            {/* Table (Chakra v3 namespace API) */}
            <Box overflowX={{ base: 'auto', md: 'hidden' }} overflow="hidden">
              <Table.Root interactive minW="720px">
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeader>Auftraggeber</Table.ColumnHeader>
                    <Table.ColumnHeader display={{ base: 'none', md: 'table-cell' }}>
                      Ansprechpartner
                    </Table.ColumnHeader>
                    <Table.ColumnHeader display={{ base: 'none', md: 'table-cell' }}>
                      Kontakt
                    </Table.ColumnHeader>
                    <Table.ColumnHeader textAlign="right">Anzahl Leistungen</Table.ColumnHeader>
                  </Table.Row>
                </Table.Header>

                <Table.Body>
                  {viewItems.length === 0 ? (
                    <Table.Row>
                      <Table.Cell colSpan={4}>
                        <EmptyState.Root>
                          <EmptyState.Content>
                            <EmptyState.Indicator>
                              <LuBuilding2 />
                            </EmptyState.Indicator>
                            <EmptyState.Title>Keine Auftraggeber gefunden</EmptyState.Title>
                            <EmptyState.Description>
                              Legen Sie einen Auftraggeber an, um ihn in Aktivitäten und Rechnungen
                              zu verwenden.
                            </EmptyState.Description>
                            <Button
                              variant="solid"
                              colorPalette="blue"
                              onClick={() => setCreateOpen(true)}
                            >
                              <LuPlus />
                              <Text>Neuen Auftraggeber anlegen</Text>
                            </Button>
                          </EmptyState.Content>
                        </EmptyState.Root>
                      </Table.Cell>
                    </Table.Row>
                  ) : (
                    viewItems.map(b => (
                      <Table.Row
                        key={b.id}
                        cursor="pointer"
                        onClick={() => {
                          setSelected(b)
                          setEditOpen(true)
                        }}
                      >
                        <Table.Cell>{b.name}</Table.Cell>
                        <Table.Cell display={{ base: 'none', md: 'table-cell' }}>
                          {b.contactDetails?.contactName || '—'}
                        </Table.Cell>
                        <Table.Cell display={{ base: 'none', md: 'table-cell' }}>
                          {b.contactDetails?.contactEmail || '—'}
                        </Table.Cell>
                        <Table.Cell textAlign="right">{b.bankServices?.length ?? 0}</Table.Cell>
                      </Table.Row>
                    ))
                  )}
                </Table.Body>
              </Table.Root>

              <Separator />

              {/* Pagination */}
              {showPagination && (
                <Flex p={3} justify="center" align="center" gap={2} flexWrap="wrap">
                  <IconButton
                    aria-label="prev"
                    variant="ghost"
                    onClick={() => canPrev && setPage(p => Math.max(1, p - 1))}
                    disabled={!canPrev}
                  >
                    <LuChevronLeft />
                  </IconButton>

                  {(() => {
                    const tp = totalPages ?? Math.max(1, page + (canNext ? 1 : 0))
                    const pages: (number | 'ellipsis')[] = []
                    const push = (v: number | 'ellipsis') => pages.push(v)

                    if (tp <= 7) {
                      for (let i = 1; i <= tp; i++) push(i)
                    } else {
                      push(1)
                      if (page > 3) push('ellipsis')
                      const start = Math.max(2, page - 1)
                      const end = Math.min(tp - 1, page + 1)
                      for (let i = start; i <= end; i++) push(i)
                      if (page < tp - 2) push('ellipsis')
                      push(tp)
                    }

                    return pages.map((p, idx) =>
                      p === 'ellipsis' ? (
                        <Box key={`e-${idx}`} px={2} color="fg.muted">
                          …
                        </Box>
                      ) : (
                        <Button
                          key={p}
                          variant={p === page ? 'solid' : 'outline'}
                          onClick={() => setPage(p)}
                        >
                          {p}
                        </Button>
                      )
                    )
                  })()}

                  <IconButton
                    aria-label="next"
                    variant="ghost"
                    onClick={() => canNext && setPage(p => p + 1)}
                    disabled={!canNext}
                  >
                    <LuChevronRight />
                  </IconButton>
                </Flex>
              )}
            </Box>
          </Flex>
        </FormLayout.Root>
      </Flex>

      {/* EDIT DIALOG */}
      <CreateEditBankDialog
        isOpen={editOpen}
        onOpenChange={setEditOpen}
        mode="edit"
        initialBank={selected ?? undefined}
        serviceTypes={serviceOptions}
        onEnsureService={ensureService}
        isProtected={isProtectedBank}
        onDeleted={async () => {
          setEditOpen(false)
          setSelected(null)
          await reloadBanks({ page })
        }}
        onSaved={() => reloadBanks({ page })}
        onSubmit={async data => {
          if (!selected) return 'error'
          if (selected.id === PROTECTED_BANK_ID) {
            // Union: contact details only. Prices are versioned with an effective date and are
            // saved per service by the dialog (SIB-941); the bank-level update must not carry
            // them, because that path writes prices without a date and drops omitted services.
            const minimalPayload: BankPartialUpdatePayload = {
              contactDetails: (data as any).contactDetails,
            }
            await updateBank(selected.id, minimalPayload as any)
          } else {
            await updateBank(selected.id, data as any)
          }
          await reloadBanks({ page })
          return 'ok'
        }}
      />

      {/* CREATE DIALOG */}
      <CreateEditBankDialog
        isOpen={createOpen}
        onOpenChange={setCreateOpen}
        mode="create"
        initialBank={{
          name: '',
          street: '',
          streetNumber: '',
          postal: '',
          city: '',
          country: '',
          autoInvoice: false,
          contactDetails: { contactName: '', contactEmail: '', contactPhone: '' },
          bankServices: [],
        }}
        serviceTypes={serviceOptions.filter(st => st.value !== 'TOJ')}
        onEnsureService={ensureService}
        onSubmit={async (data: Partial<CreateBankPayload>) => {
          const fullPayload = data as CreateBankPayload
          await createBank({ ...fullPayload, parentId: 0 } as any)
          await reloadBanks({ page: 1 })
          setPage(1)
          return 'ok'
        }}
      />
    </FormLayout.Root>
  )
}
